import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteShell from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useApp } from "@/contexts/AppContext";
import UpgradeModal from "@/components/site/UpgradeModal";
import { CheckCircle2, ArrowRight, Sparkles, Crown } from "lucide-react";

const FREE_FEATURES = [
    "Up to 5 products",
    "Product photos",
    "Public business website",
    "WhatsApp & Call buttons",
    "Google Maps directions",
    "Shareable link + QR code",
];
const PRO_FEATURES = [
    "Unlimited products",
    "Product photos",
    "Public business website",
    "Shareable Bizlinkk link",
    "QR code",
    "Basic analytics",
    "AI content editing",
    "Hosting & SSL",
];

export default function PricingPage() {
    const { t } = useI18n();
    const { user } = useApp();
    const navigate = useNavigate();
    const [showUpgrade, setShowUpgrade] = useState(false);

    const startPro = () => {
        if (user) setShowUpgrade(true);
        else navigate("/signup");
    };

    return (
        <SiteShell>
            <section className="relative pt-32 md:pt-40 pb-10 px-4 sm:px-6 text-center">
                <div className="max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-[13px] text-muted-foreground">
                        <Sparkles className="w-3.5 h-3.5 text-accent" /> {t("pricing_pill")}
                    </div>
                    <h1 className="font-heading mt-6 text-4xl sm:text-5xl font-semibold tracking-tight">{t("pricing_hero_title")}</h1>
                    <p className="mt-4 text-muted-foreground text-lg">{t("pricing_hero_sub")}</p>
                </div>
            </section>

            <section className="relative px-4 sm:px-6 pb-16">
                <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5 items-start">
                    {/* Free */}
                    <div className="relative rounded-3xl p-8 glass" data-testid="plan-free">
                        <div className="font-heading text-xl font-semibold">Free</div>
                        <div className="mt-3 flex items-end gap-1.5">
                            <span className="font-heading text-4xl font-semibold">₹0</span>
                            <span className="text-muted-foreground mb-1">forever</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Everything you need to get online.</div>
                        <ul className="mt-6 space-y-3">
                            {FREE_FEATURES.map((f) => (
                                <li key={f} className="flex items-start gap-2.5 text-sm">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <Link to="/signup"><Button className="btn-accent rounded-full w-full" data-testid="plan-free-cta">{t("start_free")} <ArrowRight className="ml-1 w-4 h-4" /></Button></Link>
                        </div>
                    </div>

                    {/* Pro */}
                    <div className="relative rounded-3xl p-8 glass-strong ring-2 ring-accent/40" data-testid="plan-pro">
                        <span className="absolute -top-3 left-8 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide bg-accent text-white px-3 py-1 rounded-full">
                            <Crown className="w-3 h-3" /> {t("most_popular")}
                        </span>
                        <div className="font-heading text-xl font-semibold">Bizlinkk Pro</div>
                        <div className="mt-3 flex items-end gap-1.5">
                            <span className="font-heading text-4xl font-semibold">₹99</span>
                            <span className="text-muted-foreground mb-1">for your first month</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Then ₹199/month. Cancel anytime.</div>
                        <ul className="mt-6 space-y-3">
                            {PRO_FEATURES.map((f) => (
                                <li key={f} className="flex items-start gap-2.5 text-sm">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <Button onClick={startPro} className="btn-accent rounded-full w-full" data-testid="plan-pro-cta">
                                <Sparkles className="w-4 h-4 mr-1.5" /> Start Pro for ₹99
                            </Button>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-3 text-center">
                            Online payment is being connected — you won't be charged yet.
                        </p>
                    </div>
                </div>
                <p className="text-center text-sm text-muted-foreground mt-8 max-w-xl mx-auto">{t("pricing_disclaimer")}</p>
            </section>

            <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
        </SiteShell>
    );
}
